import { graphql, PageProps } from "gatsby";
import React from "react";
import Breadcrumbs from "../components/Breadcrumbs";
import { DisclaimerRoute, DisclaimerRouteProps } from "../components/DisclaimerRoute";
import DocumentHead, { PageSeoType } from "../components/DocumentHead/DocumentHead";
import FormSection from "../components/FormSection";
import Hero from "../components/Hero";
import Layout from "../components/Layout";
import { MainPageHeading } from "../components/MainPageHeading";
import PortableText from "../components/PortableText";
import { PortableTextColorTheme } from "../components/PortableText/PortableText";
import { GatsbyPageContext } from "../models/pages/GatsbyPageContext";
import { isNullOrWhiteSpace } from "../shared/string";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Article = ({ data, pageContext }: PageProps<any, GatsbyPageContext>) => {
  const { article } = data;

  const DisclaimerWrapper = pageContext.behindDisclaimer
    ? DisclaimerRoute
    : React.Fragment;

  const disclaimerWrapperProps: DisclaimerRouteProps | null = pageContext.behindDisclaimer
    ? {
        lang: pageContext.i18nLang
      }
    : null;

  return (
    <DisclaimerWrapper {...disclaimerWrapperProps}>
      <Layout
        pageContext={pageContext}
        hero={<Hero image={article.heroImage} titleOverlay={article.title} />}
        breadcrumbs={<Breadcrumbs startNode={article} />}
      >
        <DocumentHead
          pageData={article}
          pageSeoType={PageSeoType.Article}
          canonical={pageContext.currentPath}
        />

        {!article.heroImage && <MainPageHeading>{article.title}</MainPageHeading>}

        {(!isNullOrWhiteSpace(article.preambleTitle) ||
          !isNullOrWhiteSpace(article.preambleText)) && (
          <aside>
            {!isNullOrWhiteSpace(article.preambleTitle) && (
              <h2 className="text-brand-primary-20 mb-4 lg:mb-8">
                {article.preambleTitle}
              </h2>
            )}
            {!isNullOrWhiteSpace(article.preambleText) && (
              <p className="text-lg lg:text-2xl">{article.preambleText}</p>
            )}
          </aside>
        )}

        {article._rawPortableTextContent && (
          <PortableText
            _rawBody={article._rawPortableTextContent}
            colorTheme={PortableTextColorTheme.Neutral}
          />
        )}

        <FormSection
          forms={article.forms}
          stepBySteps={article.stepBySteps}
          stepByStepTitle={article.stepByStepTitle}
          title={article.contactFormTitle}
        />
      </Layout>
    </DisclaimerWrapper>
  );
};

export const query = graphql`
  query GetArticleQuery($_id: String!) {
    article: sanityArticle(_id: { eq: $_id }) {
      title
      preambleTitle
      preambleText
      _updatedAt
      publishDate
      slug {
        current
      }
      _rawParentLink(resolveReferences: { maxDepth: 3 })
      heroImage {
        ...SanityImageExtFragment
      }
      _rawPortableTextContent(resolveReferences: { maxDepth: 7 })
      contactFormTitle
      forms {
        ...SanityFormTemplateFragment
        ...SanityPriceCalculatorFormTemplateFragment
      }
      stepByStepTitle
      stepBySteps
      hideBehindDisclaimer
      seo {
        ...SanitySeoPropertiesFragment
      }
      socialMedia {
        ...SanitySocialMediaPropertiesFragment
      }
    }
  }
`;

export default Article;
